import { useEffect, useState } from "react";
import {
  ApiRequestError,
  getSavedFilters,
  previewFilters,
  saveFilters,
} from "../lib/client";
import {
  COMMON_FILTER_FIELDS,
  COUNTED_OBJECTS,
  FILTER_OPERATORS,
  filterOperatorDef,
  SOURCE_OBJECTS,
} from "../lib/salesforce-schema";
import type { FilterCounts, SalesforceFilter } from "../lib/types";

/** Editable row: `filter_value` is always held as text; coerced on submit. */
interface Row {
  source_object: string;
  filter_field: string;
  filter_operator: string;
  valueText: string;
}

const DEFAULT_ROW: Row = {
  source_object: "Account",
  filter_field: "",
  filter_operator: "known",
  valueText: "",
};

// Turn a stored filter (value may be string | string[] | null) into a Row.
function toRow(f: SalesforceFilter): Row {
  const v = f.filter_value;
  return {
    source_object: f.source_object ?? "",
    filter_field: f.filter_field ?? "",
    filter_operator: f.filter_operator ?? "known",
    valueText: Array.isArray(v) ? v.join(", ") : (v ?? ""),
  };
}

/** Build the API filter for a row, coercing the value per the operator's mode. */
function toFilter(row: Row): SalesforceFilter {
  const mode = filterOperatorDef(row.filter_operator)?.valueMode ?? "single";
  let filter_value: SalesforceFilter["filter_value"] = null;
  if (mode === "list") {
    filter_value = row.valueText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  } else if (mode === "single") {
    const v = row.valueText.trim();
    filter_value = v === "" ? null : v;
  }
  return {
    source_object: row.source_object,
    filter_field: row.filter_field.trim(),
    filter_operator: row.filter_operator,
    filter_value,
  };
}

type Busy = null | "loading" | "previewing" | "saving";

/**
 * Read/update a subscriber's Salesforce import filters. Loads the stored set,
 * lets you build filter rows with documented operator/object dropdowns, then
 * preview live counts (no persist) or save (persist + counts).
 */
export function FiltersEditor({ subscriberId }: { subscriberId: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState<Busy>("loading");
  const [counts, setCounts] = useState<FilterCounts | null>(null);
  const [countsLabel, setCountsLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy("loading");
    setError(null);
    setCounts(null);
    getSavedFilters(subscriberId)
      .then((filters) => {
        if (cancelled) return;
        setRows(filters.length ? filters.map(toRow) : []);
        setBusy(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setBusy(null);
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Failed to load saved filters.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [subscriberId]);

  function updateRow(i: number, patch: Partial<Row>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setSaved(false);
  }
  function addRow() {
    setRows((rs) => [...rs, { ...DEFAULT_ROW }]);
    setSaved(false);
  }
  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
    setSaved(false);
  }

  // A row with no object can't be turned into a valid filter, and saving one
  // would drop it into the stored set as an unscoped clause. Require both.
  const validRows = rows.filter(
    (r) => r.filter_field.trim() !== "" && r.source_object !== "",
  );
  const unknownObjectRows = rows.filter((r) => r.source_object === "").length;

  async function run(kind: "previewing" | "saving") {
    setBusy(kind);
    setError(null);
    setSaved(false);
    const filters = validRows.map(toFilter);
    try {
      const result =
        kind === "saving"
          ? await saveFilters(subscriberId, filters)
          : await previewFilters(subscriberId, filters);
      setCounts(result);
      setCountsLabel(kind === "saving" ? "saved" : "preview");
      if (kind === "saving") setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : `Failed to ${kind === "saving" ? "save" : "preview"} filters.`,
      );
    } finally {
      setBusy(null);
    }
  }

  if (busy === "loading") {
    return <div className="placeholder">Loading filters…</div>;
  }

  return (
    <section className="panel">
      <p className="hint">
        Import filters restrict which Salesforce records are pulled in. Counts
        are tallied for <code>{COUNTED_OBJECTS.join(" & ")}</code> only —
        filters on other objects still save but don't affect the numbers below.
      </p>

      {rows.length === 0 ? (
        <div className="placeholder">
          No filters yet. Add one to get started.
        </div>
      ) : (
        <div className="filter-rows">
          {rows.map((row, i) => {
            const opDef = filterOperatorDef(row.filter_operator);
            const mode = opDef?.valueMode ?? "single";
            const counted = COUNTED_OBJECTS.includes(row.source_object);
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: filter rows have no stable id
              <div className="filter-row" key={i}>
                <div className="filter-grid">
                  <label className="field compact">
                    <span className="field-label">Object</span>
                    <select
                      className="select"
                      value={row.source_object}
                      onChange={(e) =>
                        updateRow(i, { source_object: e.target.value })
                      }
                    >
                      {/*
                        A stored source_object outside SOURCE_OBJECTS (custom
                        objects, EmailMessage) matches no <option>, so the browser
                        would render the select blank or snap to the first entry —
                        either way showing the wrong object. Carry the real value
                        as its own option so it stays visible and selected.
                      */}
                      {!SOURCE_OBJECTS.some(
                        (o) => o.value === row.source_object,
                      ) && (
                        <option value={row.source_object}>
                          {row.source_object === ""
                            ? "— unknown —"
                            : `${row.source_object} (not counted)`}
                        </option>
                      )}
                      {SOURCE_OBJECTS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                          {o.counted ? "" : " (not counted)"}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="field compact">
                    <span className="field-label">Field</span>
                    <input
                      className="text-input"
                      list="filter-fields"
                      spellCheck={false}
                      placeholder="e.g. Id"
                      value={row.filter_field}
                      onChange={(e) =>
                        updateRow(i, { filter_field: e.target.value })
                      }
                    />
                  </label>

                  <label className="field compact">
                    <span className="field-label">Operator</span>
                    <select
                      className="select"
                      value={row.filter_operator}
                      onChange={(e) =>
                        updateRow(i, { filter_operator: e.target.value })
                      }
                    >
                      {FILTER_OPERATORS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="field compact">
                    <span className="field-label">Value</span>
                    <input
                      className="text-input"
                      spellCheck={false}
                      disabled={mode === "none"}
                      placeholder={
                        mode === "none"
                          ? "— not needed —"
                          : mode === "list"
                            ? "comma-separated"
                            : "value"
                      }
                      value={row.valueText}
                      onChange={(e) =>
                        updateRow(i, { valueText: e.target.value })
                      }
                    />
                  </label>

                  <button
                    type="button"
                    className="btn row-remove"
                    title="Remove filter"
                    onClick={() => removeRow(i)}
                  >
                    ✕
                  </button>
                </div>
                <span className="hint">
                  {opDef?.help}
                  {!counted && " · This object isn't included in the counts."}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <datalist id="filter-fields">
        {COMMON_FILTER_FIELDS.map((f) => (
          <option key={f.value} value={f.value}>
            {f.help}
          </option>
        ))}
      </datalist>

      <div className="toolbar">
        <button
          type="button"
          className="btn"
          onClick={addRow}
          disabled={busy !== null}
        >
          + Add filter
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => run("previewing")}
          disabled={busy !== null}
        >
          {busy === "previewing" ? "Previewing…" : "Preview counts"}
        </button>
        <button
          type="button"
          className="btn primary"
          onClick={() => run("saving")}
          disabled={busy !== null}
        >
          {busy === "saving" ? "Saving…" : "Save filters"}
        </button>
        {saved && <span className="hint ok-text">Saved.</span>}
      </div>

      {unknownObjectRows > 0 && (
        <div className="banner error">
          {unknownObjectRows} filter{unknownObjectRows === 1 ? "" : "s"} loaded
          with no <code>source_object</code>. Pick an object for{" "}
          {unknownObjectRows === 1 ? "it" : "them"} or remove{" "}
          {unknownObjectRows === 1 ? "it" : "them"} — saving now drops{" "}
          {unknownObjectRows === 1 ? "it" : "them"} from the stored set.
        </div>
      )}

      {counts && (
        <div className="banner ok counts-banner">
          <strong>Counts ({countsLabel}):</strong> Accounts{" "}
          <code>{counts.accounts ?? "—"}</code> · Contacts{" "}
          <code>{counts.contacts ?? "—"}</code>
        </div>
      )}

      {error && <div className="banner error">{error}</div>}
    </section>
  );
}
